import { CloseOutlined, RightOutlined, SaveOutlined } from "@ant-design/icons";
import { Button, Form, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useParams } from "react-router-dom";
import { MeasurementTypeDto } from "../api/client-axios";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { CrudModals } from "../components/CrudModals";
import { CrudToolbar } from "../components/CrudToolbar";
import { EditorPanel } from "../components/EditorPanel";
import { EmptyState } from "../components/EmptyState";
import { PageHeader } from "../components/PageHeader";
import { TableSkeleton } from "../components/TableSkeleton";
import { LocalizedFields } from "../components/LocalizedFields";
import { TranslationStatus } from "../components/TranslationStatus";
import { useColumnSearch } from "../components/useColumnSearch";
import {
    addMeasurementType,
    clearErrorMeasurementType,
    deleteMeasurementType,
    fetchMeasurementTypes,
    selectMeasurementType,
    startAddMeasurementType,
    startDeleteMeasurementType,
    updateMeasurementType
} from "../features/measurementTypesSlice";
import { localizationEnabled } from "../config/locales";
import { measurementTypeFieldKeys, measurementTypeFields } from "../model/localizedEntities";
import { allLocalizedValues } from "../utils/translations";

export const MeasurementTypesPage = () => {

    const dispatch = useAppDispatch()
    const { t } = useTranslation();
    const measurementTypes = useAppSelector(state => state.measurementTypes.measurementTypes);
    const selectedMeasurementType = useAppSelector(state => state.measurementTypes.selectedMeasurementType);
    const status = useAppSelector(state => state.measurementTypes.status);
    const error = useAppSelector(state => state.measurementTypes.error);
    const params = useParams();

    useEffect(() => {
        dispatch(fetchMeasurementTypes());
    }, []);

    const columnSearch = useColumnSearch<MeasurementTypeDto>({
        placeholder: t("placeholder.searchMeasurementType"),
        getSearchValues: measurementType => allLocalizedValues(measurementType, measurementTypeFieldKeys)
    });

    if (params.measurementTypeId !== undefined) {
        return <Outlet />
    }

    const onRow = (measurementType: MeasurementTypeDto) => ({
        onClick: () => {
            dispatch(selectMeasurementType(measurementType))
        },
    });

    const columns: ColumnsType<MeasurementTypeDto> = [
        {
            title: t("column.description"),
            dataIndex: 'description',
            key: 'description',
            ...columnSearch,
            sorter: (a, b) => a.description! > b.description! ? -1 : 1,
            ellipsis: true
        },
        {
            width: 200,
            title: localizationEnabled ? t("column.translations") : "",
            key: 'translations',
            render: (_, measurementType) => (
                <div className="row-trailing">
                    {localizationEnabled
                        ? <TranslationStatus entity={measurementType} fields={measurementTypeFieldKeys} />
                        : <span />}
                    <Link
                        className="row-drill"
                        to={measurementType.id!}
                        onClick={event => event.stopPropagation()}
                        aria-label={t("page.measurementValueTypes.title")}
                    >
                        <RightOutlined />
                    </Link>
                </div>
            )
        }
    ];

    const getRowClassName = (measurementType: MeasurementTypeDto) => {
        return selectedMeasurementType != null && selectedMeasurementType.id === measurementType.id ? 'bg-selected' : '';
    }

    const onSaveMeasurementType = () => {
        if (selectedMeasurementType?.description == null) return;

        if (selectedMeasurementType.id == null) {
            dispatch(addMeasurementType(selectedMeasurementType));
            return;
        }

        dispatch(updateMeasurementType(selectedMeasurementType));
    }

    const canSave = selectedMeasurementType?.description != null
        && selectedMeasurementType.description.trim().length > 0;

    const addForm = status === "adding" ? (
        <EditorPanel
            title={t(selectedMeasurementType?.id != null ? "editor.edit" : "editor.new")}
            actions={
                <>
                    <Button
                        type="text"
                        icon={<CloseOutlined />}
                        onClick={() => dispatch(clearErrorMeasurementType())}
                    >
                        {t("actions.cancel")}
                    </Button>
                    <Button
                        type="primary"
                        disabled={!canSave}
                        icon={<SaveOutlined />}
                        onClick={onSaveMeasurementType}
                    >
                        {t("actions.save")}
                    </Button>
                </>
            }
        >
            <Form name="basic" layout="vertical" autoComplete="off">
                <LocalizedFields
                    fields={measurementTypeFields}
                    value={selectedMeasurementType ?? {}}
                    onChange={measurementType => dispatch(selectMeasurementType(measurementType))}
                />
            </Form>
        </EditorPanel>
    ) : null;

    const content = status === 'loading' || status === 'idle'
        ? <TableSkeleton />
        : <>
            {status !== "adding" ? <CrudToolbar
                hasSelection={selectedMeasurementType != null}
                canEdit={selectedMeasurementType?.id != null}
                selectionLabel={selectedMeasurementType?.description}
                onDelete={() => { if (selectedMeasurementType != null) dispatch(startDeleteMeasurementType()); }}
                onEdit={() => { if (selectedMeasurementType != null) dispatch(startAddMeasurementType(selectedMeasurementType)); }}
                onAdd={() => dispatch(startAddMeasurementType({} as MeasurementTypeDto))}
                onReload={() => dispatch(fetchMeasurementTypes())}
                onClearSelection={() => dispatch(selectMeasurementType(undefined))}
            /> : null}
            {addForm}
            <div className="card card--flush">
                <Table
                    rowKey={(measurementType: MeasurementTypeDto) => measurementType.id!}
                    rowClassName={getRowClassName}
                    onRow={onRow}
                    dataSource={measurementTypes}
                    columns={columns}
                    locale={{ emptyText: <EmptyState /> }}
                    pagination={{ hideOnSinglePage: true, defaultPageSize: 12, showSizeChanger: false }}
                />
            </div>

            <CrudModals
                status={status}
                error={error}
                entityKey="measurementType"
                deleteTarget={selectedMeasurementType?.description}
                onDismiss={() => dispatch(clearErrorMeasurementType())}
                onConfirmDelete={() => { if (selectedMeasurementType != null) dispatch(deleteMeasurementType(selectedMeasurementType.id!)); }}
            />
        </>

    return (
        <>
            <PageHeader
                title={t("page.measurementTypes.title")}
                count={measurementTypes?.length}
            />
            {content}
        </>
    );
}
